import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowDown, Instagram } from "lucide-react";
import WhatsAppIcon from "./WhatsAppIcon";
import { ease } from "./Reveal";
import { goTo, WHATSAPP_URL, INSTAGRAM_URL } from "@/lib/scroll";

const LINES = ["Você merece uma", "médica que te olhe", "%TODO%", "e não só para o sintoma."];

function Line({ content, i }) {
  return (
    <span className="block overflow-hidden pb-1 -mb-1">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.15, ease, delay: 0.25 + i * 0.16 }}
      >
        {content.includes("%TODO%") ? (
          <em className="font-serifd italic font-medium text-sun">como um todo</em>
        ) : (
          content
        )}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden bg-olive text-cream">
      <div className="mx-auto grid max-w-[1240px] gap-14 px-5 md:px-10 pt-32 md:pt-40 pb-24 md:pb-32 lg:grid-cols-[1.28fr_0.72fr] items-center lg:gap-8">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="mb-7 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs md:text-sm font-semibold uppercase tracking-[0.3em] text-sun"
          >
            Neurologia integrativa
            <span className="inline-block size-1.5 rounded-full bg-terra" />
            <span className="text-cream/70">Barra da Tijuca · Rio de Janeiro</span>
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display font-medium text-[clamp(1.95rem,4.6vw,4.25rem)] leading-[1.08] tracking-[-0.015em]"
          >
            {LINES.map((l, i) => (
              <Line key={i} content={l} i={i} />
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 1.05 }}
            className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-cream/75"
          >
            Na consulta, eu escuto sua história inteira, explico cada etapa em linguagem simples e
            saímos com um plano. Sem pressa e sem termos complicados.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 1.25 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="group inline-flex items-center gap-3 rounded-full bg-terra px-8 py-4 text-base md:text-lg font-bold text-cream transition-all duration-300 hover:-translate-y-1 hover:bg-terra-deep hover:shadow-xl hover:shadow-terra/30"
            >
              <WhatsAppIcon className="size-5 transition-transform duration-300 group-hover:rotate-12" />
              Agendar pelo WhatsApp
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-instagram"
              className="inline-flex items-center gap-3 rounded-full border border-cream/30 px-8 py-4 text-base md:text-lg font-semibold text-cream transition-all duration-300 hover:border-sun hover:text-sun"
            >
              <Instagram className="size-5" />
              @marianecprado
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.6 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-cream/60"
          >
            <span className="inline-flex items-center gap-2">
              <MapPin className="size-4 text-sun/80" />
              Rua Dalcídio Jurandir, 255 · sala 159
            </span>
            <span className="font-display italic text-cream/70">CRM-RJ 0114708-0</span>
          </motion.div>
        </div>

        <motion.div style={{ y: yImg }} className="relative mx-auto w-full max-w-[420px]">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.4, ease, delay: 0.55 }}
            className="relative overflow-hidden rounded-t-[999px] rounded-b-[28px] aspect-[3/4] ring-1 ring-cream/20"
            data-testid="hero-photo"
          >
            <img
              src="img/hero.jpg"
              alt="Dra. Mariane Prado segurando um modelo de cérebro"
              className="size-full object-cover scale-[1.07]"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-olive/10 rounded-t-[999px] rounded-b-[28px]" />
          </motion.div>

          <motion.div
            style={{ y: yBadge }}
            className="absolute -left-6 md:-left-12 bottom-10 size-28 md:size-32"
          >
            <motion.div
              initial={{ scale: 0, rotate: -30 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.9, ease, delay: 1.35 }}
              className="relative size-full"
            >
              <div className="absolute inset-0 rounded-full bg-sun shadow-xl shadow-olive-deep/40" />
              <svg viewBox="0 0 100 100" className="absolute inset-0 size-full spin-slow" aria-hidden="true">
                <defs>
                  <path id="circ" d="M50,50 m-35,0 a35,35 0 1,1 70,0 a35,35 0 1,1 -70,0" />
                </defs>
                <text fontSize="10" letterSpacing="3.2" fill="#2d3d1f" fontWeight="700" fontFamily="Josefin Sans">
                  <textPath href="#circ">ESCUTA · INVESTIGAÇÃO ·</textPath>
                </text>
              </svg>
              <span className="absolute left-1/2 top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-terra" />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#sintomas"
        onClick={goTo("#sintomas")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:inline-flex text-cream/50 hover:text-sun transition-colors"
        aria-label="Rolar para a próxima seção"
        data-testid="hero-scroll-hint"
      >
        <ArrowDown className="size-5 animate-bounce" />
      </motion.a>
    </section>
  );
}
